import { MarkdownHeadingPreset } from "@/constants/componentPresets";
import type { FontWeight } from "@/theme/typography";
import { adjustWeightForHebrew } from "../Cmarkdown/markdown-hebrew";
import {
  DEFAULT_MARKDOWN_HEADING_PRESET,
  MARKDOWN_HEADING_PRESET_BY_LEVEL,
  MARKDOWN_HEADING_PRESET_CONFIGS,
} from "./markdown-heading-config";
import type {
  MarkdownHeadingLevel,
  MarkdownHeadingPresetConfig,
  MarkdownHeadingTextAlign,
} from "./markdown-heading-types";

export enum MarkdownHeadingLanguageMode {
  Default = "default",
  Hebrew = "hebrew",
}

interface SelectMarkdownHeadingModelInput {
  preset?: MarkdownHeadingPreset;
  level?: MarkdownHeadingLevel;
  textAlign?: MarkdownHeadingTextAlign;
  hebrew: boolean;
}

interface SelectMarkdownHeadingPresetInput {
  preset?: MarkdownHeadingPreset;
  level?: MarkdownHeadingLevel;
}

interface ResolveHeadingWeightInput {
  weight: FontWeight;
  languageMode: MarkdownHeadingLanguageMode;
}

export interface MarkdownHeadingModel {
  config: MarkdownHeadingPresetConfig;
  languageMode: MarkdownHeadingLanguageMode;
  resolvedWeight: FontWeight;
  resolvedAlign?: MarkdownHeadingTextAlign;
}

const DEFAULT_TEXT_ALIGN_BY_LANGUAGE_MODE: Record<
  MarkdownHeadingLanguageMode,
  MarkdownHeadingTextAlign | undefined
> = {
  [MarkdownHeadingLanguageMode.Default]: undefined,
  [MarkdownHeadingLanguageMode.Hebrew]: "right",
};

const HEADING_WEIGHT_RESOLVER_BY_LANGUAGE_MODE: Record<
  MarkdownHeadingLanguageMode,
  (weight: FontWeight) => FontWeight
> = {
  [MarkdownHeadingLanguageMode.Default]: (weight) => weight,
  [MarkdownHeadingLanguageMode.Hebrew]: adjustWeightForHebrew,
};

export function selectMarkdownHeadingModel({
  preset,
  level,
  textAlign,
  hebrew,
}: SelectMarkdownHeadingModelInput): MarkdownHeadingModel {
  const finalPreset = selectMarkdownHeadingPreset({ preset, level });
  const config = MARKDOWN_HEADING_PRESET_CONFIGS[finalPreset];
  const languageMode = selectMarkdownHeadingLanguageMode({ hebrew });

  return {
    config,
    languageMode,
    resolvedWeight: resolveHeadingWeight({
      weight: config.weight,
      languageMode,
    }),
    resolvedAlign: textAlign ?? DEFAULT_TEXT_ALIGN_BY_LANGUAGE_MODE[languageMode],
  };
}

function selectMarkdownHeadingPreset({
  preset,
  level,
}: SelectMarkdownHeadingPresetInput): MarkdownHeadingPreset {
  if (preset) {
    return preset;
  }

  if (!level) {
    return DEFAULT_MARKDOWN_HEADING_PRESET;
  }

  return MARKDOWN_HEADING_PRESET_BY_LEVEL[level];
}

function selectMarkdownHeadingLanguageMode({
  hebrew,
}: {
  hebrew: boolean;
}): MarkdownHeadingLanguageMode {
  if (hebrew) {
    return MarkdownHeadingLanguageMode.Hebrew;
  }

  return MarkdownHeadingLanguageMode.Default;
}

function resolveHeadingWeight({
  weight,
  languageMode,
}: ResolveHeadingWeightInput): FontWeight {
  return HEADING_WEIGHT_RESOLVER_BY_LANGUAGE_MODE[languageMode](weight);
}
